import { useTheme } from '@mui/material/styles';
import { useCurrentTenant } from '@qpr/client-library';
import { ScaleSVG } from '@visx/responsive';
import { Bar } from '@visx/shape';
import { useTooltip } from '@visx/tooltip';
import React, { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CanvasLayoutElement, getTooltipData } from '../views/canvas-layout-view-model';
import Tooltip, { TooltipData } from './Tooltip';

export interface CanvasLayoutDataProps {
  data: CanvasLayoutElement[];
}

const Layout = ({ data }: CanvasLayoutDataProps) => {
  const theme = useTheme();
  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0
  } = useTooltip<TooltipData>({
    // initial tooltip state
    tooltipOpen: false,
    tooltipData: { attributes: [] }
  });
  const { symbol } = useCurrentTenant();
  const { search } = useLocation();
  //  tooltip tooltipDelay in milliseconds
  //  const tooltipDelay=0;
  const tooltipDelay = 300;
  let tooltipDelayed = !!tooltipDelay;
  let tooltipTimeout: number;
  let movedOutOfText = false;

  const displayTooltip = (ttLeft: number, ttTop: number, id: string | null) => {
    if (!tooltipDelayed)
      showTooltip({
        tooltipLeft: ttLeft,
        tooltipTop: ttTop,
        tooltipData: getTooltipData(id && id.substring(id.indexOf('PG')))
      });
  };

  const handleMoveTooltip = useCallback(
    (event: React.PointerEvent) => {
      if (!tooltipDelayed) {
        displayTooltip(event.pageX || 0, event.pageY || 0, event.currentTarget.getAttribute('id'));
      }
    },
    [showTooltip]
  );

  const handleMoveTooltipOutOfText = useCallback(
    (event: React.PointerEvent) => {
      movedOutOfText = true;
      displayTooltip(event.pageX, event.pageY, event.currentTarget.getAttribute('id'));
    },
    [showTooltip]
  );

  const handleDelayTooltip = useCallback(
    (event: React.PointerEvent<SVGRectElement>) => {
      if (tooltipDelay && !movedOutOfText) {
        hideTooltip();
        tooltipDelayed = true;
        const id = event.currentTarget.getAttribute('id');
        if (tooltipTimeout) clearTimeout(tooltipTimeout);
        tooltipTimeout = window.setTimeout(() => {
          tooltipDelayed = false;
          displayTooltip(event.pageX || 0, event.pageY || 0, id);
        }, tooltipDelay);
      }
      movedOutOfText = false;
    },
    [showTooltip]
  );

  const calcSvgHeight = () => {
    const h =
      data
        .map(bar => Number(bar.graphicalproperties.height) + Number(bar.graphicalproperties.y))
        .sort((a, b) => a - b)[data.length - 1] + 2000;
    return Number.isNaN(h) ? 0 : Number(h);
  };
  const calcSvgWidth = () => {
    let w: number =
      Number(
        data
          .map(bar => Number(bar.graphicalproperties.width) + Number(bar.graphicalproperties.x))
          .sort((a, b) => a - b)[data.length - 1]
      ) + Number(data.map(bar => bar.graphicalproperties.x).sort((a, b) => (a > 0 ? a - b : b))[0]);
    if (w < 1000) w = 1000;
    if (w > 5000) w = 2000;
    return Number.isNaN(w) ? 0 : Number(w);
  };

  return (
    <>
      <ScaleSVG width={calcSvgWidth()} height={calcSvgHeight()}>
        <filter id="drop-shadow">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2.2" />
          <feOffset dx="0" dy="2" result="offsetblur" />
          <feFlood floodColor="rgba(0,0,0,0.2)" />
          <feComposite in2="offsetblur" operator="in" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <rect
          width={calcSvgWidth()}
          height={calcSvgHeight()}
          className="customCanvasBackground"
          onPointerMove={hideTooltip}
          fill="white"
        />
        {data.map(bar => {
          const typename = bar.typename?.replace(/ /g, '-').toLowerCase();
          return (
            <Link
              to={`/${symbol}/maps/${bar.id}/${search}`}
              key={bar.id}
              id={`link-${bar.id}`}
              onClick={hideTooltip}
            >
              <Bar
                className={`customCanvasBar customCanvasBar-${typename}`}
                x={bar.graphicalproperties.x}
                y={bar.graphicalproperties.y}
                width={bar.graphicalproperties.width}
                height={bar.graphicalproperties.height}
                rx={4}
                fill={`${theme?.palette.secondary.light}`}
                stroke={`${theme?.palette.secondary.dark}`}
                strokeWidth="1"
                filter="url(#drop-shadow)"
              />
              <foreignObject
                x={bar.graphicalproperties.x}
                y={bar.graphicalproperties.y}
                width={bar.graphicalproperties.width}
                height={bar.graphicalproperties.height}
                onPointerMove={handleMoveTooltip}
                onPointerOver={handleDelayTooltip}
                id={`bar-${bar.id}`}
                name={`${bar.name}`}
              >
                <div
                  className={`customCanvasText customCanvasText-${typename}`}
                  id={`text-${bar.id}`}
                  onPointerMove={handleMoveTooltip}
                  onPointerOver={handleMoveTooltipOutOfText}
                >
                  {bar.name}
                </div>
              </foreignObject>
            </Link>
          );
        })}
      </ScaleSVG>
      {tooltipOpen && <Tooltip left={tooltipLeft} top={tooltipTop} data={tooltipData} />}
    </>
  );
};

export default Layout;
